import { Inject, Injectable, signal } from '@angular/core';
import {
  Payout,
  Milestone,
  Contract,
  Statement,
  TrackStatement,
  Notification,
  Ticket,
  Territory,
  RevenueBreakdown,
  PlatformPerformance,
  PlatformDetail,
  TrackDetail,
  UserRole,
  ContractDetails
} from '../models/navigation.model';
import { filter, map, Observable, switchMap, take, tap } from 'rxjs';
import { GenericResponseDto } from '../models/generic-dto.model';
import { HttpClient } from '@angular/common/http';
import { ApiUrls } from '../constants/api-urls';
import { AuthService, User } from './auth.service';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  PaginatedStatementsResponse,
  PlatformDetailsResponse,
  TrackDetailsResponse,
  MonthlyEarning,
  Period,
  PaymentSummary
} from '../models/royalty.model';
import { ContractRevenuePayload, MonthDetailPayload, AllTracksPayload } from '../views/dashboard/dashboard-revenue.models';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  authService = Inject(AuthService);
  userRole = signal<UserRole>('artist');
  // currentUser = signal<User | null>(null);
  private currentUser$: Observable<User | null> = toObservable(this.authService.currentUser);

  constructor(private http: HttpClient) {

  }

  // Dashboard Data
  getRecentPayouts(): Payout[] {
    return [
      { platform: 'Spotify', amount: '$3,240', date: 'Oct 23, 2025', status: 'completed' },
      { platform: 'Apple Music', amount: '$2,180', date: 'Oct 22, 2025', status: 'completed' },
      { platform: 'YouTube Music', amount: '$890', date: 'Oct 20, 2025', status: 'pending' }
    ];
  }

  getUpcomingMilestones(): Milestone[] {
    return [
      { title: 'Album Release: "Echoes"', date: 'Nov 15, 2025', type: 'release' },
      { title: 'Tour Payment Due', date: 'Nov 1, 2025', type: 'payment' },
      { title: 'Contract Renewal: Universal', date: 'Dec 10, 2025', type: 'contract' }
    ];
  }


  // Contracts Data
  getContracts(providerId: number): Observable<Contract[]> {
    return this.http.get<GenericResponseDto>(
      `${ApiUrls.CONTRACTS}/${providerId}`,
      {}
    ).pipe(
      map(res => res.result as Contract[])
    );
  }
  getContractDetails(providerId: number): Observable<ContractDetails[]> {
    return this.http.get<GenericResponseDto>(
      `${ApiUrls.CONTRACT_Details}/${providerId}`,
      {}
    ).pipe(
      map(res => res.result as ContractDetails[])
    );
  }

  getRevenueTimeline(contractId: number): Observable<ContractRevenuePayload> {
    return this.http.get<GenericResponseDto>(
      `${ApiUrls.CONTRACT_REVENUE_TIMELINE}/${contractId}/timeline`
    ).pipe(
      map(res => res.result as ContractRevenuePayload)
    );
  }

  getRevenueMonthDetail(contractId: number, year: number, month: number): Observable<MonthDetailPayload> {
    return this.http.get<GenericResponseDto>(
      `${ApiUrls.CONTRACT_REVENUE_DETAIL}/${contractId}/detail`,
      { params: { year: year.toString(), month: month.toString() } }
    ).pipe(
      map(res => res.result as MonthDetailPayload)
    );
  }

  getRevenueAllTracks(contractId: number): Observable<AllTracksPayload> {
    return this.http.get<GenericResponseDto>(
      `${ApiUrls.CONTRACT_REVENUE_ALL_TRACKS}/${contractId}/tracks`
    ).pipe(
      map(res => res.result as AllTracksPayload)
    );
  }

  /**
   * Download contract file as blob
   * @param contractId - The ID of the contract to download
   * @returns Observable<Blob> - The contract file as a blob
   */
  downloadContract(contractId: number): Observable<Blob> {
    return this.http.get(
      `${ApiUrls.CONTRACTS_DOWNLOAD}/${contractId}`,
      {
        responseType: 'blob'  // Critical: tells Angular to expect binary data
      }
    ) as Observable<Blob>;
  }

  // Royalties Data
  getStatements(
    providerId: number,
    page: number = 1,
    pageSize: number = 10,
    period?: string,
    search?: string
  ): Observable<PaginatedStatementsResponse> {
    const params: any = {
      providerContractId: providerId,
      page,
      pageSize
    };

    if (period && period !== 'all') {
      params.period = period;
    }

    if (search) {
      params.search = search;
    }

    return this.http
      .get<GenericResponseDto>(ApiUrls.ROYALTIES_STATEMENTS, { params })
      .pipe(
        map(response => {
          if (response.isSucceeded && response.result) {
            return response.result as PaginatedStatementsResponse;
          }
          throw new Error('Failed to load royalty statements');
        })
      );
  }

getPaymentSummary(partyId: number): Observable<PaymentSummary> {
  return this.http
    .get<GenericResponseDto>(ApiUrls.ROYALTIES_PAYMENTS, {
      params: { partyId: partyId }
    })
    .pipe(
      map(response => {
        if (response.isSucceeded && response.result) {
          return response.result as PaymentSummary;
        }

        throw new Error('Failed to load payment summary');
      })
    );
}
  // Notifications Data
  getNotifications(): Notification[] {
    return [
      {
        type: 'alert',
        title: 'Contract Expiring Soon',
        message: 'Your Live Nation tour agreement expires in 15 days.',
        time: '2 hours ago',
        unread: true
      },
      {
        type: 'payment',
        title: 'Payment Received',
        message: 'Spotify royalty payment of $3,240 has been processed.',
        time: '5 hours ago',
        unread: true
      },
      {
        type: 'support',
        title: 'Support Ticket Updated',
        message: 'Your dispute case #1247 has been reviewed. See response.',
        time: '1 day ago',
        unread: true
      },
      {
        type: 'system',
        title: 'Analytics Report Ready',
        message: 'Your monthly analytics report for October 2025 is ready to view.',
        time: '2 days ago',
        unread: false
      },
      {
        type: 'milestone',
        title: 'Milestone Completed',
        message: 'Album "Echoes" has reached 1M streams!',
        time: '3 days ago',
        unread: false
      }
    ];
  }

  // Support Data
  getTickets(): Ticket[] {
    return [
      {
        id: '1247',
        subject: 'Royalty Dispute - Missing Streams',
        status: 'In Progress',
        priority: 'High',
        created: 'Oct 20, 2025',
        sla: '2 days remaining'
      },
      {
        id: '1246',
        subject: 'Contract Amendment Request',
        status: 'Pending Review',
        priority: 'Medium',
        created: 'Oct 18, 2025',
        sla: '4 days remaining'
      },
      {
        id: '1245',
        subject: 'Payment Inquiry - Delayed Payout',
        status: 'Resolved',
        priority: 'High',
        created: 'Oct 15, 2025',
        sla: 'Completed'
      }
    ];
  }

  // Analytics Data
  getTerritories(): Territory[] {
    return [
      { country: 'United States', streams: '4.2M', revenue: '$15,840', flag: '🇺🇸' },
      { country: 'United Kingdom', streams: '2.8M', revenue: '$10,240', flag: '🇬🇧' },
      { country: 'Germany', streams: '1.9M', revenue: '$7,180', flag: '🇩🇪' },
      { country: 'Canada', streams: '1.4M', revenue: '$5,240', flag: '🇨🇦' },
      { country: 'Australia', streams: '980K', revenue: '$3,840', flag: '🇦🇺' }
    ];
  }

  getRevenueBreakdown(): RevenueBreakdown[] {
    return [
      { type: 'Streaming', amount: '$42,180', percentage: 72 },
      { type: 'Downloads', amount: '$8,240', percentage: 14 },
      { type: 'Physical Sales', amount: '$5,180', percentage: 9 },
      { type: 'Sync Licensing', amount: '$2,840', percentage: 5 }
    ];
  }

  getPlatformPerformance(): PlatformPerformance[] {
    return [
      { platform: 'Spotify', streams: '5.2M', revenue: '$18,420', share: '45%', color: 'bg-green-500' },
      { platform: 'Apple Music', streams: '3.8M', revenue: '$14,240', share: '32%', color: 'bg-red-500' },
      { platform: 'YouTube Music', streams: '2.1M', revenue: '$8,180', share: '18%', color: 'bg-red-600' },
      { platform: 'Amazon Music', streams: '890K', revenue: '$3,240', share: '5%', color: 'bg-blue-500' }
    ];
  }

  // Platform Details Data
  getPlatformDetails(
    platform: string,
    period: string,
    providerId: number,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    sortBy?: string
  ): Observable<PlatformDetailsResponse> {
    const params: any = {
      providerContractId: providerId,
      page,
      pageSize
    };

    if (search) {
      params.search = search;
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    return this.http
      .get<GenericResponseDto>(
        `${ApiUrls.ROYALTIES_PLATFORM_DETAILS}/${encodeURIComponent(platform)}/${encodeURIComponent(period)}`,
        { params }
      )
      .pipe(
        map(response => {
          if (response.isSucceeded && response.result) {
            return response.result as PlatformDetailsResponse;
          }
          throw new Error('Failed to load platform details');
        })
      );
  }

  // Track Details Data
  getTrackDetails(
    track: string,
    period: string,
    providerId: number,
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    sortBy?: string
  ): Observable<TrackDetailsResponse> {
    const params: any = {
      providerContractId: providerId,
      page,
      pageSize
    };

    if (search) {
      params.search = search;
    }

    if (sortBy) {
      params.sortBy = sortBy;
    }

    return this.http
      .get<GenericResponseDto>(
        `${ApiUrls.ROYALTIES_TRACK_DETAILS}/${encodeURIComponent(track)}/${encodeURIComponent(period)}`,
        { params }
      )
      .pipe(
        map(response => {
          if (response.isSucceeded && response.result) {
            return response.result as TrackDetailsResponse;
          }
          throw new Error('Failed to load track details');
        })
      );
  }

  getMonthlyEarnings(providerId: number): Observable<MonthlyEarning[]> {
    const params = {
      providerContractId: providerId
    };

    return this.http
      .get<GenericResponseDto>(ApiUrls.ROYALTIES_EARNINGS, { params })
      .pipe(
        map(response => {
          if (response.isSucceeded && response.result) {
            return response.result as MonthlyEarning[];
          }
          throw new Error('Failed to load monthly earnings');
        })
      );
  }

  getAvailablePeriods(providerId: number): Observable<Period[]> {
    const params = {
      providerContractId: providerId
    };

    return this.http
      .get<GenericResponseDto>(ApiUrls.ROYALTIES_PERIODS, { params })
      .pipe(
        map(response => {
          if (response.isSucceeded && response.result) {
            return response.result as Period[];
          }
          throw new Error('Failed to load available periods');
        })
      );
  }
}
